import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "font-mono inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-palette-grey-1000 hover:bg-palette-surface-surface-action-filled-hovered-and-focused focus:bg-palette-surface-surface-action-filled-hovered-and-focused active:bg-palette-surface-surface-action-filled-pressed text-white",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md pt-[12px] pr-4 pb-[12px] pl-6",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  text?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      iconLeft,
      iconRight,
      text,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // When asChild is true, only render children (Slot requires exactly one child)
    if (asChild) {
      return (
        <Comp
          className={cn(
            "flex items-center gap-spacing-3",
            buttonVariants({ variant, size, className }),
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    // When asChild is false, render with icons and text
    return (
      <Comp
        className={cn(
          "flex items-center gap-spacing-3",
          buttonVariants({ variant, size, className }),
        )}
        ref={ref}
        {...props}
      >
        {iconLeft && <span className="flex items-center">{iconLeft}</span>}
        {text && (
          <span className="details-Medium-Medium-Uppercase text-palette-text-invert">
            {text}
          </span>
        )}
        {iconRight && <span className="flex items-center">{iconRight}</span>}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
